import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardContent, PageTitle } from 'shared-components';
import { Button, ButtonIntent, ButtonTier } from 'shared-components';
import { FilterBar } from '../filter-bar/filter-bar';
import { Pager } from '../pager/pager';
import Routes from '../../app/routes';
import {
  useCreateSelectorTemplateMutation,
  useDeleteSelectorTemplateMutation,
  useGetSelectorTemplatesQuery,
} from './product-selectors.api';
import {
  IProductSelectorTemplate,
  IProductSelectorTree,
  ISelectorNode,
} from '@topshots/core-models';
import styles from './product-selectors.module.css';

const buildEmptyTree = (): IProductSelectorTree => {
  const rootId = `node_${Math.random().toString(36).slice(2, 8)}`;
  const rootNode: ISelectorNode = {
    id: rootId,
    prompt: 'New selector prompt',
    options: [],
  };

  return {
    rootNodeId: rootId,
    nodes: {
      [rootId]: rootNode,
    },
  };
};

const buildNewTemplate = (): Partial<IProductSelectorTemplate> => ({
  name: 'New Product Selector',
  tree: buildEmptyTree(),
});

export const SelectorTemplates = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  const navigate = useNavigate();

  const { data, isLoading } = useGetSelectorTemplatesQuery({
    page,
    perPage,
    search,
    includeArchived,
  });
  const [createTemplate] = useCreateSelectorTemplateMutation();
  const [deleteTemplate] = useDeleteSelectorTemplateMutation();

  const templates = useMemo(() => data?.data ?? [], [data]);

  if (isLoading) {
    return <span>loading...</span>;
  }

  return (
    <>
      <PageTitle
        title="Product Selectors"
        crumbs={[
          { display: Routes.DASHBOARD.title, link: Routes.DASHBOARD.link },
          { display: Routes.PRODUCTS.title, link: Routes.PRODUCTS.link },
          {
            display: Routes.PRODUCT_SELECTORS.title,
            link: Routes.PRODUCT_SELECTORS.link,
          },
        ]}
        furtherActions={[{ value: 'create', display: 'Create Template' }]}
        onAction={async (action) => {
          switch (action) {
            case 'create':
              try {
                const response = (await createTemplate(buildNewTemplate())) as {
                  data: IProductSelectorTemplate;
                };
                toast.success('Selector template created!', {
                  autoClose: 2000,
                });
                navigate(
                  Routes.PRODUCT_SELECTOR.createLink?.(
                    response.data._id.toString()
                  ) ?? ''
                );
              } catch (err) {
                toast.error('Failed to create template');
              }
              break;
          }
        }}
      />
      <Card>
        <CardContent>
          <FilterBar
            value={{ includeArchived: includeArchived ? 'true' : 'false' }}
            filters={[
              {
                key: 'includeArchived',
                title: 'Include Archived',
                options: [
                  { display: 'No', value: 'false' },
                  { display: 'Yes', value: 'true' },
                ],
              },
            ]}
            onFilterChanged={(filter, values) => {
              if (filter.key === 'includeArchived') {
                setIncludeArchived(values === 'true');
              }
            }}
            onSearch={(term) => {
              setSearch(term === '' ? undefined : term);
            }}
          />
        </CardContent>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Questions</th>
              <th>Start Question</th>
              <th>Archived</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((template) => (
              <tr
                key={template._id.toString()}
                className={styles.tableRow}
                onClick={() => {
                  navigate(
                    Routes.PRODUCT_SELECTOR.createLink?.(
                      template._id.toString()
                    ) ?? ''
                  );
                }}
              >
                <td>{template.name}</td>
                <td>{Object.keys(template.tree.nodes).length}</td>
                <td>
                  {template.tree.nodes[template.tree.rootNodeId]?.prompt ||
                    template.tree.rootNodeId}
                </td>
                <td>{template.isArchived ? 'Yes' : 'No'}</td>
                <td className={styles.actionsCell}>
                  {!template.isArchived && (
                    <Button
                      tier={ButtonTier.Tertiary}
                      intent={ButtonIntent.Negative}
                      onClick={async (event) => {
                        event.stopPropagation();
                        if (
                          window.confirm(
                            'Archive this selector template? This cannot be undone.'
                          )
                        ) {
                          await deleteTemplate(template._id.toString());
                          toast.success('Selector template archived', {
                            autoClose: 2000,
                          });
                        }
                      }}
                    >
                      Archive
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager
          onNext={() => {
            setPage(page + 1);
          }}
          onPrevious={() => {
            setPage(page - 1);
          }}
          onPageSelected={(selected) => {
            setPage(selected);
          }}
          onPerPageChange={(selected) => {
            setPerPage(selected);
            setPage(1);
          }}
          page={page}
          perPage={perPage}
          totalPages={data?.totalPages ?? 1}
        />
      </Card>
    </>
  );
};
